import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom'; // NEW: Import Link for routing

// Material-UI Core Components
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography'; // FIXED: Corrected import

import megaMenuData from './menuData'; // Adjust path if needed
import MegaMenu from './MegaMenu'; // Adjust path if needed

// Material-UI Hooks for Responsiveness
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

// Material-UI Icons
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';

import logo from '../assets/Logo/logobella.png';

// --- Component ---

const AtlasBar = () => {
  const { t } = useTranslation();

  // --- State Management ---
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activePopover, setActivePopover] = useState(null);
  const [popoverAnchorEl, setPopoverAnchorEl] = useState(null);

  // --- Responsiveness ---
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // --- Handlers ---
  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  
  const handlePopoverOpen = (event, popoverKey) => {
    setPopoverAnchorEl(event.currentTarget);
    setActivePopover(popoverKey);
  };
  
  const handlePopoverClose = () => {
    setActivePopover(null);
  };

  // --- Scroll Effect ---
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // --- Style Definitions ---
  const navLinkColor = '#2E3836';
  
  // NEW: Data structure for navigation links including their paths
  const navLinks = [
    { key: 'nav_destinations', path: '/destinations' },
    { key: 'nav_travel_styles', path: '/trips' },
    { key: 'nav_our_journal', path: '/blogs' },
    { key: 'nav_why_bella_ceyon', path: '/about-us' },
  ];

  const navLinkStyle = {
    fontFamily: "'Lato', sans-serif",
    fontWeight: 400,
    fontSize: '1rem',
    color: navLinkColor,
    textTransform: 'none',
    '&:hover': {
      backgroundColor: 'transparent',
      color: '#D4A259',
    },
  };

  // --- Sub-components for Readability ---
  const desktopNav = (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1, gap: 2 }}>
    {navLinks.map(({ key, path }) => { // Looping over the new navLinks array
      const isMegaMenu = !!megaMenuData[key];
      return (
         <Box 
           key={key} 
           onMouseLeave={isMegaMenu ? handlePopoverClose : undefined}
         >
           <Button
            component={Link}
            to={path}
            aria-owns={isMegaMenu && activePopover === key ? `${key}-popover` : undefined}
            aria-haspopup={isMegaMenu ? 'true' : 'false'}
            onMouseEnter={(event) => isMegaMenu && handlePopoverOpen(event, key)}
            sx={navLinkStyle}
          >
            {t(key)}
          </Button>
          {isMegaMenu && (
            <Popover
              id={`${key}-popover`}
              open={activePopover === key}
              anchorEl={popoverAnchorEl}
              onClose={handlePopoverClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              disableRestoreFocus
              PaperProps={{
                sx: { 
                  overflow: 'visible',
                  backgroundColor: 'transparent', 
                  boxShadow: '0 8px 32px rgba(46, 56, 54, 0.15)',
                  borderRadius: '8px',
                },
              }}
            >
              {/* THIS IS THE IMPORTANT CHANGE */}
              <MegaMenu content={megaMenuData[key]} basePath={path} />
            </Popover>
          )}
        </Box>
      );
    })}
  </Box>
  );

  const mobileDrawer = (
    <Drawer
      anchor="right"
      open={mobileOpen}
      onClose={handleDrawerToggle}
      ModalProps={{ keepMounted: true }}
      sx={{ '& .MuiDrawer-paper': { boxSizing: 'border-box', width: '80%', maxWidth: 300, backgroundColor: '#FCF9F3' } }}
    >
      <Box onClick={handleDrawerToggle} sx={{ textAlign: 'center' }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
          <IconButton><CloseIcon sx={{ color: '#2E3836' }} /></IconButton>
        </Box>
        <List>
          {navLinks.map(({ key, path }) => ( // UPDATED: Looping over the new navLinks array
            <ListItem key={key} disablePadding>
              <ListItemButton
                // NEW: Added component and to props for navigation
                component={Link}
                to={path}
                sx={{ textAlign: 'center' }}
              >
                <ListItemText
                  primary={t(key)}
                  primaryTypographyProps={{ fontFamily: "'Lato', sans-serif", color: '#2E3836', fontWeight: 700 }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
        <Divider sx={{ my: 2, mx: 2 }} />
        <Box sx={{ p: 2 }}>
          <Button
            variant="contained"
            fullWidth
            component={Link}
            to="/plan-your-trip"
            sx={{ fontFamily: "'Lato', sans-serif", backgroundColor: '#D4A259', color: '#FCF9F3', '&:hover': { backgroundColor: '#b88b4a' } }}
          >
            {t('cta_plan_voyage')}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );

  // --- Render ---
  return (
    <>
      <AppBar
        position={isScrolled ? 'fixed' : 'absolute'}
        elevation={0}
        sx={{
          backgroundColor: '#FCF9F3',
          left: 0,
          right: 0,
          top: isScrolled ? 0 : '40px',
          transition: theme.transitions.create(['box-shadow', 'top'], {
            duration: theme.transitions.duration.short,
          }),
          boxShadow: isScrolled ? '0 2px 10px rgba(46, 56, 54, 0.1)' : 'none',
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', height: '80px' }}>
          <Box component="a" href="/" sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
            <img src={logo} alt="Bella Ceyon Voyages Logo" style={{ height: '100px', width: 'auto' }} />
            {/* <Typography fontSize={20} sx={{ fontFamily:'"Cormorant Garamond", "Georgia", serif', color:'#2E3836',marginLeft:'-21px'  }}>Bella Ceylon Voyages</Typography> */}
          </Box>

          {isMobile ? (
            <IconButton aria-label="open drawer" edge="end" onClick={handleDrawerToggle} sx={{ color: navLinkColor }}>
              <MenuIcon />
            </IconButton>
          ) : (
            <>
              {desktopNav}
              <Button
                variant="contained"
                component={Link}
                to="/plan-your-trip"
                sx={{ fontFamily: "'Lato', sans-serif", fontWeight: 700, backgroundColor: '#D4A259', color: '#FCF9F3', borderRadius: '2px', px: 3, '&:hover': { backgroundColor: '#b88b4a' } }}
              >
                {t('cta_plan_voyage')}
              </Button>
            </>
          )}
        </Toolbar>
      </AppBar>
      <nav>
        {mobileDrawer}
      </nav>
    </>
  );
};

export default AtlasBar;